import { useId } from 'react';
import { Stack } from '@understory-io/leaf/stack';
import { Text } from '@understory-io/leaf/text';
import { FormattedMessage } from 'react-intl';
import { CrossSellCard } from './cross-sell-card';

export type CrossSellSectionProps = {
  /** The storefront's `giftCards` preference. */
  giftCards: boolean;
  /** The storefront's `punchcards` preference. */
  punchcards: boolean;
};

/**
 * The "Other options" row: the gift-card and punch-card tiles, under a shared
 * heading.
 *
 * A whole section rather than the bare tiles, because the heading, the row
 * layout and the "show nothing when the operator sells neither" rule belong
 * together — the home page and the upcoming-events list both mount this and
 * neither has to re-derive them.
 */
export function CrossSellSection({
  giftCards,
  punchcards,
}: CrossSellSectionProps) {
  // Name the <section> via its heading so it is exposed as an ARIA `region`
  // with an accessible name. Called before the early return so the hook order
  // stays stable.
  const headingId = useId();

  if (!giftCards && !punchcards) return null;

  return (
    <Stack as="section" gap="large" aria-labelledby={headingId}>
      <Text as="h2" id={headingId} style="subheadline-small" color="tertiary">
        <FormattedMessage
          defaultMessage="Other options"
          description="Header for the gift card and punch card upsell section on home page"
        />
      </Text>
      <Stack
        direction={{ base: 'column', tablet: 'row' }}
        gap="large"
        align="stretch"
      >
        {giftCards && <CrossSellCard kind="giftcard" />}
        {punchcards && <CrossSellCard kind="punchcard" />}
      </Stack>
    </Stack>
  );
}
